import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { CompoundEntityRef } from '@backstage/catalog-model';
import { KeyVaultApi, SetSecretResult } from './KeyVaultApi';

// Fetch-based KeyVaultApi. Uses Backstage's FetchApi so the Authorization:
// Bearer header reaches /api/key-vault automatically. There is deliberately
// no read-value method — the client surface itself can only write or list
// names.
export class KeyVaultClient implements KeyVaultApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async base(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('key-vault');
  }

  private entityPath(ref: CompoundEntityRef): string {
    return `entity/${encodeURIComponent(ref.kind)}/${encodeURIComponent(
      ref.namespace,
    )}/${encodeURIComponent(ref.name)}`;
  }

  async listSecretNames(ref: CompoundEntityRef): Promise<string[]> {
    const url = `${await this.base()}/v1/${this.entityPath(ref)}/secrets`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) {
      throw new Error(await errorMessage(res));
    }
    const body = (await res.json()) as { names: string[] };
    return body.names;
  }

  async setSecret(
    ref: CompoundEntityRef,
    secretName: string,
    secretValue: string,
  ): Promise<SetSecretResult> {
    const url = `${await this.base()}/v1/${this.entityPath(ref)}/secrets`;
    const res = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ secretName, secretValue }),
    });
    if (!res.ok) {
      throw new Error(await errorMessage(res));
    }
    return (await res.json()) as SetSecretResult;
  }
}

// Surface the Backstage error-middleware message (`{ error: { message } }`)
// when present so a 400 (e.g. an invalid secret name) reads clearly in the UI,
// falling back to the raw body / status text.
async function errorMessage(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const json = JSON.parse(text) as { error?: { message?: unknown } };
    const msg = json?.error?.message;
    if (typeof msg === 'string' && msg) return msg;
  } catch {
    // not JSON — fall through to the raw text
  }
  return text.slice(0, 500) || `HTTP ${res.status} ${res.statusText}`;
}
